// Server-derived onboarding readiness for the SPA. Backed by the dual-auth BFF
// route GET /api/surfaces/onboarding (Bearer, via apiFetch). The completion
// predicate (signedIn && (connectedSourceCount > 0 || hasApiKey)) is computed
// server-side — the extension is NOT part of it.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

export interface OnboardingReadiness {
  onboardingComplete: boolean;
  connectedSourceCount: number;
  hasApiKey: boolean;
  connectedProviders: string[];
  extensionConnected: boolean;
  // Dedicated first-run flag (Phase 1c): true once the wizard/checklist explicitly records
  // completion via POST /api/surfaces/onboarding/complete. This is the gate signal — NOT the
  // read-latched `onboardingComplete` (which derives from `onboarded_at`, latched on every
  // GET, and would hide onboarding from a user who connected a source then bailed). See
  // chooseOnboardingView + design §10.3 / D11.
  firstRunCompleted: boolean;
}

export const onboardingReadinessQueryKey = ['surfaces', 'onboarding'] as const;

/** Testable core (the queryFn): fetches readiness via the shared apiFetch client. */
export function fetchOnboardingReadiness(): Promise<OnboardingReadiness> {
  return apiFetch<OnboardingReadiness>('/api/surfaces/onboarding');
}

/**
 * Readiness hook. Pass `pollMs` to poll (used by the Claude Code branch, which
 * waits for `terum-capture setup` to mint an api key). Returns null data while
 * loading so callers branch on it the same way the surface hooks do.
 */
export function useOnboardingReadiness(options?: { pollMs?: number; enabled?: boolean }) {
  const query = useQuery({
    queryKey: onboardingReadinessQueryKey,
    queryFn: fetchOnboardingReadiness,
    staleTime: 0,
    refetchInterval: options?.pollMs ?? false,
    enabled: options?.enabled ?? true,
  });

  return {
    loading: query.isLoading,
    data: query.data ?? null,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
  };
}
